import React from 'react';
import { SignerAppReadyResponse } from '@dropbox/sign-internal-client';
import { Button } from '@dropbox/dig-components/buttons';
import { Banner } from '@dropbox/dig-components/banner';
import { TextInput } from '@dropbox/dig-components/text_fields';
import { Card } from '@dropbox/dig-components/card';
import { Box, ThemeContainer, ThemeProvider } from '@dropbox/dig-foundations';
import { WordmarkLogo, SignWordmark } from '@dropbox/dig-logos';
import { Text, Link } from '@dropbox/dig-components/typography';
import { Footer } from 'signer-app/signer-experience/footer';
import styles from 'signer-app/signer-experience/sms-auth-number.module.css';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { LockMini } from '@dropbox/dig-illustrations/dist/cjs/mini/lock';
import { useBundleProps } from 'signer-app/bundle-props';

const msg = defineMessages({
  smsSendMsg: {
    id: 'ba966fc59f4c09680b16eca0c4907fc307a19093f482e12244834510b9d5892a',
    defaultMessage:
      "To make sure it's you, we need to verify via text message to <strong>{number}</strong>",
    description: 'SMS auth send message',
  },
  securityPriority: {
    id: '4936e18bfa46550c1749f2f8ca93410a83b00ae5439eb57ca41328ce271b65d9',
    defaultMessage: 'Security is our top priority.',
    description: 'Security priority message',
  },
  sendCode: {
    id: '1d297e7777d96325f99d62694a03f20abb274c05097f1e718f819d80d7938d71',
    defaultMessage: 'Send code',
    description: 'Send SMS button',
  },
  enterCode: {
    id: '5296bba68fd0551a6482acd4e67fdea11a134ff971a488434572fa9f842240c8',
    defaultMessage: 'We just sent a text message to <strong>{number}</strong>',
    description: 'Prompt to enter SMS code',
  },
  verify: {
    id: '2fb8b0a5ffafb105322d3d5b466c3461b3975223884a88ed958e85ae7dcc4373',
    defaultMessage: 'Verify & continue',
    description: 'Verify SMS code button',
  },
  attemptsLeft: {
    id: 'c6ebb5b81d89a1821c5de04b71b2623dbf67a0d131edde625cb82a2541aacef7',
    defaultMessage:
      "The authentication code you've entered is incorrect. {count, plural, one {# attempt} other {# attempts}} remaining.",
    description: 'Attempts left message',
  },
  maxAttempts: {
    id: '2541fb18617f561cf695a14561c5c1360b7496b473378b8096a0c45342ac0539',
    defaultMessage: 'You have reached the maximum number of attempts.',
    description: 'Out of attempts message',
  },
  contactSender: {
    id: '5b8c571273071066b47df6e9f79a2ff0ba543f14ab54258187cb9b16ad49c92b',
    defaultMessage: 'Contact {name} at {email}',
    description: 'Contact sender help text',
  },
  needHelp: {
    id: '2f7e86750bdbdd89f531ea3ed0b11048ecdfce2fdfd3350cf1cfc6275456cd4a',
    defaultMessage: 'Need help?',
    description: 'Need help heading',
  },
  invalidCode: {
    id: 'sms.invalid_code',
    defaultMessage: 'The code you entered is invalid. Please try again.',
    description: 'Error message for invalid SMS code',
  },
});

interface Props {
  signerNumber: string;
  numAttemptsLeft: number | null;
  requesterName?: string | null;
  requesterEmail: string;
  onSendSms(): Promise<SignerAppReadyResponse>;
  onVerify(code: string): Promise<SignerAppReadyResponse>;
  onSuccess?(resp: SignerAppReadyResponse): void;
  isRateLimited?: boolean;
}

export const SmsAuthNumber: React.FC<Props> = ({
  signerNumber,
  numAttemptsLeft,
  requesterName,
  requesterEmail,
  onSendSms,
  onVerify,
  onSuccess,
  isRateLimited = false,
}) => {
  const intl = useIntl();
  const { customLogoUri } = useBundleProps();
  const [step, setStep] = React.useState<'send' | 'verify'>('send');
  const [code, setCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [attempts, setAttempts] = React.useState<number | null>(
    numAttemptsLeft,
  );
  const [rateLimited, setRateLimited] = React.useState<boolean>(isRateLimited);

  React.useEffect(() => {
    if (isRateLimited) {
      setRateLimited(true);
    }
  }, [isRateLimited]);

  React.useEffect(() => {
    setAttempts(numAttemptsLeft);
  }, [numAttemptsLeft]);

  const outOfAttempts = attempts !== null && attempts === 0;
  const showBanner = !!error || outOfAttempts || rateLimited;

  const bannerMessage = React.useMemo(() => {
    if (rateLimited) {
      return intl.formatMessage({
        id: 'sms.rate_limited',
        defaultMessage:
          'This phone number has been contacted too many times. Please wait a few minutes and try again.',
      });
    }
    if (outOfAttempts) {
      return intl.formatMessage(msg.maxAttempts);
    }
    return intl.formatMessage(msg.attemptsLeft, { count: attempts ?? 0 });
  }, [rateLimited, outOfAttempts, attempts, intl]);

  const handleSend = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp: SignerAppReadyResponse = await onSendSms();
      const codeResp = resp?.error?.code;
      if (codeResp === 'sms_rate_limit') {
        setRateLimited(true);
      } else if (codeResp === 'sms_auth_required' || !codeResp) {
        setRateLimited(false);
        setAttempts(resp?.error?.data?.numAttemptsLeft ?? attempts);
        setStep('verify');
      }
    } catch (e) {
      setError('Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!code) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp: SignerAppReadyResponse = await onVerify(code);
      const codeResp = resp?.error?.code;
      if (!resp?.ready && codeResp === 'sms_auth_required') {
        setAttempts(resp?.error?.data?.numAttemptsLeft ?? attempts);
        setError(intl.formatMessage(msg.invalidCode));
      } else if (codeResp === 'sms_rate_limit') {
        setRateLimited(true);
      } else if (resp?.ready) {
        onSuccess?.(resp);
      }
    } catch (e) {
      setError('Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme="vis2023">
      <ThemeContainer>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          className={styles.wrapper}
          marginTop="Macro Medium"
        >
          <Box marginBottom={showBanner ? '0' : 'Macro Small'}>
            {customLogoUri ? (
              <img
                src={customLogoUri}
                alt="Brand logo"
                style={{ height: '56px', width: 'auto', maxWidth: '200px' }}
              />
            ) : (
              <WordmarkLogo size={56} src={SignWordmark} />
            )}
          </Box>
          <Box
            maxWidth="100%"
            marginTop={showBanner ? '0' : 'Macro Medium'}
            padding={'Macro Medium'}
          >
            {showBanner && (
              <Box marginBottom="Macro Small">
                <Banner type="alert">
                  <Banner.Message>{bannerMessage}</Banner.Message>
                </Banner>
              </Box>
            )}
            <Card>
              <Card.Content spacing="large" backgroundColor="Subtle">
                <div className={styles.keyWrapper}>
                  <LockMini altText="Lock" />
                </div>
                {step === 'send' ? (
                  <>
                    <Text
                      variant="label"
                      size="large"
                      isBold={true}
                      tagName="h2"
                    >
                      <FormattedMessage
                        id="sms.verify_identity"
                        defaultMessage="Verify identity"
                        description="title text in signer flow, indicating signer will need to verify their identity"
                      />
                    </Text>
                    <Box marginBottom="Micro Small">
                      <Text>
                        <FormattedMessage {...msg.securityPriority} />
                      </Text>
                    </Box>
                    <Text>
                      <FormattedMessage
                        {...msg.smsSendMsg}
                        values={{
                          number: signerNumber,
                          strong: (str: string) => (
                            <strong key="strong">{str}</strong>
                          ),
                        }}
                      />
                    </Text>
                    <Box className={styles.buttonRow}>
                      <Button
                        variant="primary"
                        disabled={loading || outOfAttempts || rateLimited}
                        onClick={handleSend}
                      >
                        {intl.formatMessage(msg.sendCode)}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Text>
                      <FormattedMessage
                        {...msg.enterCode}
                        values={{
                          number: signerNumber,
                          strong: (str: string) => (
                            <strong key="strong">{str}</strong>
                          ),
                        }}
                      />
                    </Text>
                    <TextInput
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                    {!rateLimited && (
                      <Box marginTop="Micro Small">
                        <Text>
                          <Link
                            href="#"
                            variant="primary"
                            onClick={(e) => {
                              e.preventDefault();
                              handleSend();
                            }}
                          >
                            <FormattedMessage
                              id="sms.resend"
                              defaultMessage="Send the code again"
                              description="Resend code link"
                            />
                          </Link>
                        </Text>
                      </Box>
                    )}
                    {error && (
                      <Box marginBottom="Micro Small">
                        <Text color="error">{error}</Text>
                      </Box>
                    )}
                    <Box className={styles.buttonRow}>
                      <Button
                        variant="primary"
                        disabled={loading || outOfAttempts || rateLimited}
                        onClick={handleVerify}
                      >
                        {intl.formatMessage(msg.verify)}
                      </Button>
                    </Box>
                  </>
                )}
                <Box marginTop="Macro Medium">
                  <Text variant="label" size="medium" isBold={true} tagName="p">
                    <FormattedMessage {...msg.needHelp} />
                  </Text>
                  <Text color="subtle">
                    <FormattedMessage
                      {...msg.contactSender}
                      values={{
                        name: requesterName || '',
                        email: requesterEmail,
                      }}
                    />
                  </Text>
                </Box>
              </Card.Content>
            </Card>
          </Box>
          <Footer />
        </Box>
      </ThemeContainer>
    </ThemeProvider>
  );
};
